import type { PropertyPortalPublicationDisplayStatusFilter } from 'src/api/generated';
import { z } from 'zod';
import { globalSearchSchema } from 'src/modules/shared/schemas/search.schema';
import { PROPERTY_PUBLICATION_STATUS_FILTER_OPTIONS } from '../utils/property-publications-filters.util';

const propertyPublicationDisplayStatusFilterValues =
  PROPERTY_PUBLICATION_STATUS_FILTER_OPTIONS as [
    PropertyPortalPublicationDisplayStatusFilter,
    ...PropertyPortalPublicationDisplayStatusFilter[],
  ];

/** Schema for property-specific filter parameters */
const propertiesFiltersSchema = z.object({
  returnTo: z.enum(['publications']).optional(),
  publicationStatus: z
    .enum(propertyPublicationDisplayStatusFilterValues)
    .optional(),
  search: z.preprocess((value) => {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }

    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      typeof value === 'bigint'
    ) {
      return String(value);
    }

    return undefined;
  }, z.string().optional()),
  operation: z.enum(['ALL', 'SALE', 'RENT', 'TEMPORARY_RENT']).default('ALL'),
  activeStatus: z.enum(['ACTIVE', 'INACTIVE', 'ALL']).default('ACTIVE'),
  sortBy: z
    .enum(['UPDATED_DESC', 'CREATED_DESC', 'PRICE_DESC', 'PRICE_ASC'])
    .default('UPDATED_DESC'),
  viewMode: z.enum(['VISUAL', 'COMPACT']).default('VISUAL'),
  pageSize: z.coerce.number().int().min(10).max(100).default(20),
  userId: z.preprocess((value) => {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }, z.number().int().optional()),
  salePriceMin: z.coerce.number().optional(),
  salePriceMax: z.coerce.number().optional(),
  rentPriceMin: z.coerce.number().optional(),
  rentPriceMax: z.coerce.number().optional(),
  region: z.string().optional(),
  locality: z.string().optional(),
});

/** Combined schema for properties search page URL parameters */
export const propertiesSearchSchema = globalSearchSchema
  .extend({
    phoneVersion: globalSearchSchema.shape.phoneVersion.default('v1' as const),
  })
  .merge(propertiesFiltersSchema);

/** Type-safe properties search parameters inferred from schema */
export type PropertiesSearch = z.infer<typeof propertiesSearchSchema>;
